/**
 * Utility functions for Azure Referral Form
 */
import type {
  AzureMpcReferralType,
  DealType,
  ReferralStatus,
  ReferralSubstatus,
} from './azureReferralSchema'
import {
  AZURE_CUSTOMER_PURCHASE_INTENT,
  AZURE_DEAL_TYPE,
  AZURE_FORM_STATUS,
  AZURE_MPC_REFERRAL_TYPE,
  AZURE_REFERRAL_STATUS,
  AZURE_REFERRAL_SUBSTATUS,
  SHARED_AZURE_DEAL_TYPES,
  type AzureCustomerPurchaseIntent,
  type AzureFormStatus,
  isAzureDealType,
  isTerminalReferralSubstatus,
  matchesReferralStatus,
  matchesReferralSubstatus,
} from './azureReferralSchema'
import {
  isNonSharedDealType,
  isPrivateDealType,
  isSharedCosellDealType,
} from './dealTypeUtils'
import { getHubspotCosellAccountIdFromPath } from '../../../../common/utils/Hooks/partnerStateSelectors'
import {
  CustomerDetails,
  DealDetails,
  HelpFromMicrosoft,
  SolutionsOffered,
} from './types'

type AzureReferralMetadataSlice = {
  partnerId?: string
  partner_id?: string
  referralId?: string
  crmPrimaryKeyValue?: string
}

type ResolveAzurePartnerIdParams = {
  pathname: string
  accountIdFromPath?: string
  tenantPartnerId?: string
  azureReferralMetadata?: AzureReferralMetadataSlice
}

export const resolveAzurePartnerId = ({
  pathname,
  accountIdFromPath,
  tenantPartnerId,
  azureReferralMetadata,
}: ResolveAzurePartnerIdParams): string =>
  getHubspotCosellAccountIdFromPath(pathname) ||
  accountIdFromPath ||
  azureReferralMetadata?.partnerId ||
  azureReferralMetadata?.partner_id ||
  tenantPartnerId ||
  ''

export const isAzureReferralRouteReady = (
  referralId: string | undefined,
  filledForm: AzureReferralFormHydrationSlice,
  azureReferralMetadata?: AzureReferralMetadataSlice
): boolean =>
  Boolean(referralId) &&
  (hasAzureReferralFormMappedData(filledForm) ||
    Boolean(
      azureReferralMetadata?.referralId ||
        azureReferralMetadata?.crmPrimaryKeyValue
    ))

type AzureReferralFormHydrationSlice = {
  customerDetails?: CustomerDetails
  dealDetails?: DealDetails
}

export { AZURE_MPC_REFERRAL_TYPE }
export type { AzureMpcReferralType, DealType }

/** Canonical form value for "no Microsoft help". */
export const NO_HELP_REQUIRED = 'NoHelpRequired'

/** API / MPC value for "no Microsoft help" (maps to {@link NO_HELP_REQUIRED} in the form). */
export const API_UNKNOWN_TYPE_OF_HELP = 'Unknown'

/**
 * Canonical BE type-of-help values (engine / MPC enums).
 * UI labels live in HELP_TYPE_OPTIONS; form state stores these values.
 */
export const AZURE_TYPE_OF_HELP = {
  NO_HELP_REQUIRED: 'NoHelpRequired',
  WORKLOAD_SPECIFIC_VALUE_PROPOSITION: 'WorkloadSpecificValueProposition',
  CUSTOMER_TECHNICAL_ARCHITECTURE: 'CustomerTechnicalArchitecture',
  PROOF_OF_CONCEPT_OR_DEMO: 'ProofOfConceptOrDemo',
  QUOTES_OR_LICENSING: 'QuotesOrLicensing',
  POST_SALES_CUSTOMER_SUCCESS: 'PostSalesCustomerSuccess',
  GENERAL_OR_OTHER: 'GeneralOrOther',
} as const

export type AzureTypeOfHelpValue =
  (typeof AZURE_TYPE_OF_HELP)[keyof typeof AZURE_TYPE_OF_HELP]

const AZURE_TYPE_OF_HELP_VALUES = new Set<string>(
  Object.values(AZURE_TYPE_OF_HELP)
)

/**
 * CRM / mapping-template / legacy UI variants → canonical BE value.
 * Keys are alphanumeric-lowercased (spaces, punctuation stripped).
 */
const TYPE_OF_HELP_ALIASES: Record<string, AzureTypeOfHelpValue> = {
  nohelprequired: AZURE_TYPE_OF_HELP.NO_HELP_REQUIRED,
  nohelprequiredatthistime: AZURE_TYPE_OF_HELP.NO_HELP_REQUIRED,
  nohelprequiredatthispointoftime: AZURE_TYPE_OF_HELP.NO_HELP_REQUIRED,
  unknown: AZURE_TYPE_OF_HELP.NO_HELP_REQUIRED,
  workloadspecificvalueproposition:
    AZURE_TYPE_OF_HELP.WORKLOAD_SPECIFIC_VALUE_PROPOSITION,
  customertechnicalarchitecture:
    AZURE_TYPE_OF_HELP.CUSTOMER_TECHNICAL_ARCHITECTURE,
  proofofconceptordemo: AZURE_TYPE_OF_HELP.PROOF_OF_CONCEPT_OR_DEMO,
  proofofconceptdemo: AZURE_TYPE_OF_HELP.PROOF_OF_CONCEPT_OR_DEMO,
  quotesorlicensing: AZURE_TYPE_OF_HELP.QUOTES_OR_LICENSING,
  quoteslicensing: AZURE_TYPE_OF_HELP.QUOTES_OR_LICENSING,
  postsalescustomersuccess: AZURE_TYPE_OF_HELP.POST_SALES_CUSTOMER_SUCCESS,
  generalorother: AZURE_TYPE_OF_HELP.GENERAL_OR_OTHER,
  other: AZURE_TYPE_OF_HELP.GENERAL_OR_OTHER,
  // Replaced UI option — keep hydrating legacy saved values as GeneralOrOther
  adoptionandchangemanagement: AZURE_TYPE_OF_HELP.GENERAL_OR_OTHER,
}

const normalizeTypeOfHelpKey = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')

export const isNoMicrosoftHelpRequired = (typeOfHelp?: string): boolean => {
  if (typeof typeOfHelp !== 'string') return false
  const key = normalizeTypeOfHelpKey(typeOfHelp)
  return Boolean(key) && TYPE_OF_HELP_ALIASES[key] === NO_HELP_REQUIRED
}

/**
 * Map CRM / mapping-template / legacy UI type-of-help onto BE enum values.
 * Unknown non-empty values are returned trimmed so Select can still display them.
 */
export const normalizeAzureTypeOfHelp = (
  value: unknown
): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined
  if (AZURE_TYPE_OF_HELP_VALUES.has(trimmed)) return trimmed
  const mapped = TYPE_OF_HELP_ALIASES[normalizeTypeOfHelpKey(trimmed)]
  return mapped ?? trimmed
}

/** Map form type-of-help to the API enum (NoHelpRequired → Unknown). */
export const mapAzureTypeOfHelpToApi = (typeOfHelp: string): string =>
  isNoMicrosoftHelpRequired(typeOfHelp)
    ? API_UNKNOWN_TYPE_OF_HELP
    : typeOfHelp

export const hasAzureReferralFormMappedData = (
  filledForm: AzureReferralFormHydrationSlice
): boolean =>
  Boolean(
    filledForm.dealDetails?.dealName ||
      filledForm.customerDetails?.customerName ||
      filledForm.dealDetails?.crmId
  )

export const getAzureReferralFormikKey = (
  referralId: string | undefined,
  filledForm: AzureReferralFormHydrationSlice
): string => {
  if (!referralId) {
    return 'new-azure-referral'
  }

  return hasAzureReferralFormMappedData(filledForm)
    ? `${referralId}-hydrated`
    : `${referralId}-pending`
}

export const isAzureReferralFormLoadPending = (
  referralId: string | undefined,
  filledForm: AzureReferralFormHydrationSlice
): boolean => Boolean(referralId) && !hasAzureReferralFormMappedData(filledForm)

export const isMicrosoftHelpRequested = (typeOfHelp?: string) =>
  Boolean(typeof typeOfHelp === 'string' && typeOfHelp.trim()) &&
  !isNoMicrosoftHelpRequired(typeOfHelp)

/**
 * Deal types driven by seller visibility when no Microsoft help is requested.
 */
export const getDealTypeForSellerDealView = (
  sellerDealView: NonNullable<HelpFromMicrosoft['sellerDealView']>
): DealType =>
  sellerDealView === 'YES'
    ? AZURE_DEAL_TYPE.PARTNER_LED
    : AZURE_DEAL_TYPE.PRIVATE

/** No-help + Yes/No visibility owns Deal Type (Private / Partner-led). */
export const isDealTypeDrivenBySellerVisibility = ({
  typeOfHelp,
  sellerDealView,
}: {
  typeOfHelp?: string
  sellerDealView?: HelpFromMicrosoft['sellerDealView']
}): boolean => !isMicrosoftHelpRequested(typeOfHelp) && Boolean(sellerDealView)

export const hasAzureSolutionsSelected = (
  solutionsOffered?: Pick<SolutionsOffered, 'selectedSolutions'> | null
): boolean =>
  (solutionsOffered?.selectedSolutions?.filter(Boolean).length ?? 0) > 0

/**
 * Private/Partner-led → shared co-sell when Microsoft assistance is requested.
 * Partner role filled → Services Co-sell; otherwise → IP Co-sell.
 */
export const getDealTypeWhenRequestingMicrosoftHelp = ({
  partnerRole,
}: {
  partnerRole?: string
}): DealType =>
  partnerRole?.trim()
    ? AZURE_DEAL_TYPE.SERVICES_CO_SELL
    : AZURE_DEAL_TYPE.IP_CO_SELL

export const shouldConvertNonSharedDealWhenRequestingHelp = (
  dealType?: DealType | string
): boolean => isNonSharedDealType(dealType)

export const shouldShowPrivateToCosellConversionMessage = (
  previousDealType?: DealType | string
): boolean => isPrivateDealType(previousDealType)

export const PRIVATE_TO_COSELL_CONVERSION_MESSAGE =
  'Your deal has been converted from a Private Deal to a Co-sell opportunity because Microsoft assistance has been requested.'

/**
 * Normalize CRM / legacy / form purchase-intent values to Microsoft
 * `CustomerMarketplaceIntent` enums (also used as form radio keys).
 */
export const mapAzureCustomerPurchaseIntent = (
  value: unknown
): AzureCustomerPurchaseIntent | undefined => {
  if (typeof value !== 'string') {
    return undefined
  }
  const trimmed = value.trim()
  if (!trimmed) {
    return undefined
  }
  const normalized = trimmed.toLowerCase().replace(/_/g, ' ').replace(/-/g, ' ')
  if (normalized === 'yes') {
    return AZURE_CUSTOMER_PURCHASE_INTENT.YES
  }
  if (normalized === 'no') {
    return AZURE_CUSTOMER_PURCHASE_INTENT.NO
  }
  if (
    normalized === 'have not decided' ||
    normalized === 'havenotdecided' ||
    normalized === 'not decided'
  ) {
    return AZURE_CUSTOMER_PURCHASE_INTENT.HAVE_NOT_DECIDED
  }
  return undefined
}

export const mapFormDealTypeToReferralType = (
  dealType?: DealType | string
): AzureMpcReferralType =>
  dealType && (SHARED_AZURE_DEAL_TYPES as readonly string[]).includes(dealType)
    ? AZURE_MPC_REFERRAL_TYPE.SHARED
    : AZURE_MPC_REFERRAL_TYPE.INDEPENDENT

export { isAzureDealType }

const emptyHelpContactFields: Pick<
  HelpFromMicrosoft,
  'firstName' | 'lastName' | 'phone' | 'email'
> = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
}

/**
 * Updates Help from Microsoft when type of help changes.
 * - Help requested: clear seller visibility (follow-up is hidden).
 * - Explicit "no help": preserve seller visibility if already set (e.g. mapped);
 *   otherwise default to Yes so Microsoft sellers can view the deal.
 * - Empty type of help: preserve existing seller visibility; do not invent a
 *   default until the user chooses "no help".
 *
 * Customer contact fields are stored on `customerDetails` and shared with
 * Customer Details; they are not cleared when help type changes.
 */
export const getHelpFromMicrosoftForTypeOfHelp = ({
  helpFromMicrosoft,
  typeOfHelp,
}: {
  helpFromMicrosoft: HelpFromMicrosoft
  typeOfHelp: HelpFromMicrosoft['typeOfHelp']
}): HelpFromMicrosoft => {
  if (isMicrosoftHelpRequested(typeOfHelp)) {
    return {
      ...helpFromMicrosoft,
      typeOfHelp,
      sellerDealView: undefined,
      ...emptyHelpContactFields,
    }
  }

  return {
    ...helpFromMicrosoft,
    typeOfHelp,
    ...emptyHelpContactFields,
    sellerDealView: isNoMicrosoftHelpRequired(typeOfHelp)
      ? (helpFromMicrosoft.sellerDealView ?? 'YES')
      : helpFromMicrosoft.sellerDealView,
  }
}

export type AzureFormEditabilityOptions = {
  dealType?: DealType | string
  /**
   * Last saved Help from Microsoft type (loaded/submitted referral), not an
   * in-progress Formik selection. Used so No-help → other help stays editable
   * until the next submit.
   */
  typeOfHelp?: string
  /** Sales stage label/value; Blocked is treated as a terminal non-editable state */
  salesStage?: string | number
}

const isNewPendingReferral = (
  status?: ReferralStatus | string,
  substatus?: ReferralSubstatus | string
) =>
  matchesReferralStatus(status, AZURE_REFERRAL_STATUS.NEW) &&
  matchesReferralSubstatus(substatus, AZURE_REFERRAL_SUBSTATUS.PENDING)

const isActiveAcceptedReferral = (
  status?: ReferralStatus | string,
  substatus?: ReferralSubstatus | string
) =>
  matchesReferralStatus(status, AZURE_REFERRAL_STATUS.ACTIVE) &&
  matchesReferralSubstatus(substatus, AZURE_REFERRAL_SUBSTATUS.ACCEPTED)

const isBlockedSalesStage = (salesStage?: string | number) =>
  String(salesStage ?? '')
    .trim()
    .toLowerCase() === 'blocked'

const isErrorReferralStatus = (status?: ReferralStatus | string) =>
  status?.toLowerCase() === 'error'

/** Fields editable while Active + Accepted (post Microsoft acceptance). */
const ACTIVE_ACCEPTED_EDITABLE_FIELDS = [
  'estimatedCloseDate',
  'estimatedValue',
  'currency',
  'marketingCampaignID',
  'partnerNotes',
  'notesToMicrosoft',
  'teamMembers',
  'contactDetails',
  'solutionsOffered',
  'companySolutions',
  'microsoftSolutions',
  'selectedSolutions',
  'typeOfHelp',
  'sellerDealView',
  'salesStage',
  'status',
  'substatus',
] as const

/** Progression fields editable whenever the form itself is editable. */
const GENERAL_EDITABLE_FIELDS = [
  'dealName',
  'estimatedCloseDate',
  'estimatedValue',
  'currency',
  'partnerNotes',
  'notesToMicrosoft',
  'crmId',
  'marketingCampaignID',
  'teamMembers',
  'contactDetails',
  'solutionsOffered',
  'companySolutions',
  'microsoftSolutions',
  'selectedSolutions',
  'typeOfHelp',
  'sellerDealView',
] as const

export const isFormEditable = (
  status?: ReferralStatus | string,
  substatus?: ReferralSubstatus | string,
  formStatus?: AzureFormStatus | string,
  options?: AzureFormEditabilityOptions
): boolean => {
  if (formStatus === AZURE_FORM_STATUS.DRAFT) {
    return true
  }

  if (isBlockedSalesStage(options?.salesStage)) {
    return false
  }

  if (!status) {
    // If no status is set, allow editing (new referral create flow)
    return true
  }

  if (isErrorReferralStatus(status)) {
    return false
  }

  if (isTerminalReferralSubstatus(substatus)) {
    return false
  }

  if (matchesReferralStatus(status, AZURE_REFERRAL_STATUS.CLOSED)) {
    return false
  }

  if (isNewPendingReferral(status, substatus)) {
    // Submitted + awaiting Microsoft: freeze shared (IP / Services) deals only.
    // Private and Partner-led remain editable.
    return !isSharedCosellDealType(options?.dealType)
  }

  if (
    matchesReferralStatus(status, AZURE_REFERRAL_STATUS.NEW) &&
    matchesReferralSubstatus(substatus, AZURE_REFERRAL_SUBSTATUS.RECEIVED)
  ) {
    return true
  }

  if (isActiveAcceptedReferral(status, substatus)) {
    return true
  }

  // All other combinations — read-only
  return false
}

/**
 * Whether the Azure referral form has been submitted (no longer a fresh create/draft).
 */
export const isAzureReferralFormSubmitted = (
  formStatus?: AzureFormStatus | string,
  status?: ReferralStatus | string,
  substatus?: ReferralSubstatus | string
): boolean => {
  if (formStatus === AZURE_FORM_STATUS.DRAFT) {
    return false
  }

  if (formStatus === AZURE_FORM_STATUS.SUBMITTED) {
    return true
  }

  if (status || substatus) {
    return true
  }

  return false
}

export const hasAzureContactFieldValue = (
  fieldValue?: string | number | null
): boolean => String(fieldValue ?? '').trim() !== ''

/**
 * Help from Microsoft contact fields lock after submit only when they already
 * have a value; empty fields stay editable regardless of deal type.
 */
export const isHelpFromMicrosoftContactFieldEditable = (
  formStatus?: AzureFormStatus | string,
  status?: ReferralStatus | string,
  substatus?: ReferralSubstatus | string,
  fieldValue?: string | number | null
): boolean => {
  if (!isAzureReferralFormSubmitted(formStatus, status, substatus)) {
    return true
  }

  return !hasAzureContactFieldValue(fieldValue)
}

/**
 * Type of help editability (`typeOfHelp` = last saved value, not the current
 * unsaved selection):
 * - Before submit / draft: always editable.
 * - After submit + Microsoft help already saved: always read-only.
 * - After submit + no help saved: editable only while Active + Accepted
 *   (read-only while Pending and other pre-acceptance states). Changing to
 *   another help type in the UI stays editable until the next submit.
 */
export const isHelpFromMicrosoftTypeOfHelpEditable = ({
  formStatus,
  status,
  substatus,
  typeOfHelp,
}: {
  formStatus?: AzureFormStatus | string
  status?: ReferralStatus | string
  substatus?: ReferralSubstatus | string
  typeOfHelp?: string
}): boolean => {
  if (!isAzureReferralFormSubmitted(formStatus, status, substatus)) {
    return true
  }

  if (isMicrosoftHelpRequested(typeOfHelp)) {
    return false
  }

  return isActiveAcceptedReferral(status, substatus)
}

/**
 * Creation / setup fields (customer, deal type, solution area, etc.).
 * Locked once Microsoft has accepted the referral (Active + Accepted).
 */
export const areAzureReferralCreationFieldsEditable = (
  status?: ReferralStatus | string,
  substatus?: ReferralSubstatus | string,
  formStatus?: AzureFormStatus | string,
  options?: AzureFormEditabilityOptions
): boolean => {
  if (!isFormEditable(status, substatus, formStatus, options)) {
    return false
  }

  return !isActiveAcceptedReferral(status, substatus)
}

/**
 * Determines if a specific field is editable based on status and field name
 */
export const isFieldEditable = (
  status?: ReferralStatus | string,
  substatus?: ReferralSubstatus | string,
  fieldName?: string,
  formStatus?: AzureFormStatus | string,
  options?: AzureFormEditabilityOptions
): boolean => {
  if (!isFormEditable(status, substatus, formStatus, options)) {
    return false
  }

  // If no field name provided, use general form editability
  if (!fieldName) {
    return true
  }

  if (fieldName === 'typeOfHelp') {
    return isHelpFromMicrosoftTypeOfHelpEditable({
      formStatus,
      status,
      substatus,
      typeOfHelp: options?.typeOfHelp,
    })
  }

  if (isActiveAcceptedReferral(status, substatus)) {
    return (ACTIVE_ACCEPTED_EDITABLE_FIELDS as readonly string[]).includes(
      fieldName
    )
  }

  return (GENERAL_EDITABLE_FIELDS as readonly string[]).includes(fieldName)
}

/**
 * Seller visibility may be enabled after acceptance (Private → Partner-led),
 * but cannot be removed once set to visible.
 * Draft form status (including New/Pending drafts) stays fully editable.
 * Submitted + no-help + consent Yes is always locked (cannot revoke sharing).
 */
export const canChangeAzureSellerDealView = ({
  status,
  substatus,
  formStatus,
  dealType,
  sellerDealView,
  typeOfHelp,
}: {
  status?: ReferralStatus | string
  substatus?: ReferralSubstatus | string
  formStatus?: AzureFormStatus | string
  dealType?: DealType | string
  sellerDealView?: HelpFromMicrosoft['sellerDealView']
  typeOfHelp?: string
}): boolean => {
  const options = { dealType }
  if (!isFormEditable(status, substatus, formStatus, options)) {
    return false
  }

  // Draft create/edit: treat like a fresh form — Yes/No remain changeable even
  // when backend already stamped New/Pending on the referral record.
  if (formStatus === AZURE_FORM_STATUS.DRAFT) {
    return true
  }

  // Submitted + no help + consent to share: radios stay disabled.
  if (
    formStatus === AZURE_FORM_STATUS.SUBMITTED &&
    isNoMicrosoftHelpRequired(typeOfHelp) &&
    sellerDealView === 'YES'
  ) {
    return false
  }

  const hasExistingReferral = Boolean(status || substatus)
  if (!hasExistingReferral) {
    return true
  }

  if (!isActiveAcceptedReferral(status, substatus)) {
    return false
  }

  // Active + Accepted: allow enabling visibility only while still Private / No.
  return sellerDealView === 'NO' || dealType === AZURE_DEAL_TYPE.PRIVATE
}

/**
 * Gets a user-friendly message explaining why the form is read-only*
 */
export const getReadOnlyMessage = (
  status?: ReferralStatus | string,
  substatus?: ReferralSubstatus | string,
  formStatus?: AzureFormStatus | string,
  options?: AzureFormEditabilityOptions
): string => {
  if (isFormEditable(status, substatus, formStatus, options)) {
    return ''
  }

  if (isBlockedSalesStage(options?.salesStage)) {
    return 'This referral is blocked and cannot be edited.'
  }

  if (matchesReferralSubstatus(substatus, AZURE_REFERRAL_SUBSTATUS.WON)) {
    return 'This referral has been won and cannot be edited.'
  }

  if (matchesReferralSubstatus(substatus, AZURE_REFERRAL_SUBSTATUS.LOST)) {
    return 'This referral has been lost and cannot be edited.'
  }

  if (matchesReferralSubstatus(substatus, AZURE_REFERRAL_SUBSTATUS.DECLINED)) {
    return 'This referral has been declined and cannot be edited.'
  }

  if (
    matchesReferralSubstatus(substatus, AZURE_REFERRAL_SUBSTATUS.ERROR) ||
    isErrorReferralStatus(status)
  ) {
    return 'This referral has an error and cannot be edited.'
  }

  if (matchesReferralSubstatus(substatus, AZURE_REFERRAL_SUBSTATUS.EXPIRED)) {
    return 'This referral has expired and cannot be edited. Referrals automatically expire after 14 days of inactivity.'
  }

  if (matchesReferralStatus(status, AZURE_REFERRAL_STATUS.CLOSED)) {
    return 'This referral is closed and cannot be edited.'
  }

  if (isNewPendingReferral(status, substatus)) {
    return 'This referral has been submitted to Microsoft and cannot be edited while it is pending review.'
  }

  return 'This referral is not in an editable state.'
}

export {
  isActiveAcceptedReferral,
  isNewPendingReferral,
  isSharedCosellDealType,
}
